import type { FixedCost, Row } from "./types.ts";
import { isoWeek, newId } from "./format.ts";

export type InLedger = "no" | "active" | "snoozed";

export type FixedCostCandidate = {
  row: Row;
  inLedger: InLedger;
};

type YearMonth = { year: number; month: number };

const ID_PREFIX = "r_fix_";
const MONTH = /^(\d{4})-(\d{2})$/;

function parseMonth(month: string): YearMonth | null {
  const m = MONTH.exec(month);
  if (!m) return null;
  const parsed = { year: Number(m[1]), month: Number(m[2]) };
  return parsed.month >= 1 && parsed.month <= 12 ? parsed : null;
}

function requireMonth(month: string): YearMonth {
  const parsed = parseMonth(month);
  if (!parsed) throw new Error(`Ogiltig månad "${month}"`);
  return parsed;
}

export function isValidMonth(month: string): boolean {
  return parseMonth(month) !== null;
}

function daysInMonth({ year, month }: YearMonth): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function monthIndex({ year, month }: YearMonth): number {
  return year * 12 + month - 1;
}

export function fixedCostRowId(costId: string, month: string): string {
  return `${ID_PREFIX}${month}_${costId}`;
}

function baseId(rowId: string): string {
  const dot = rowId.indexOf(".");
  return dot === -1 ? rowId : rowId.slice(0, dot);
}

export function dueDateInMonth(dayOfMonth: number, month: string): string {
  const day = Math.min(dayOfMonth, daysInMonth(requireMonth(month)));
  return `${month}-${String(day).padStart(2, "0")}`;
}

export function isDueInMonth(cost: FixedCost, month: string): boolean {
  const ym = requireMonth(month);
  const dueDate = dueDateInMonth(cost.dayOfMonth, month);
  if (cost.startDate && cost.startDate > dueDate) return false;
  if (cost.endDate && cost.endDate < dueDate) return false;
  if (cost.occurrence === "monthly") return true;
  const startMonth =
    cost.startMonth ??
    (cost.startDate ? Number(cost.startDate.slice(5, 7)) : 1);
  const anchor = monthIndex({ year: ym.year, month: startMonth });
  const period = cost.occurrence === "quarterly" ? 3 : 12;
  return (((monthIndex(ym) - anchor) % period) + period) % period === 0;
}

export function toLedgerRow(cost: FixedCost, month: string): Row {
  const dueDate = dueDateInMonth(cost.dayOfMonth, month);
  return {
    id: fixedCostRowId(cost.id, month),
    week: isoWeek(dueDate),
    dueDate,
    supplier: cost.description,
    amount: -(Number(cost.amount) || 0),
    note: cost.category,
    snoozed: false,
  };
}

function ledgerStatusByBaseId(
  existing: Pick<Row, "id" | "snoozed">[],
): Map<string, InLedger> {
  const status = new Map<string, InLedger>();
  for (const r of existing) {
    if (!r.id.startsWith(ID_PREFIX)) continue;
    const id = baseId(r.id);
    if (status.get(id) !== "active") {
      status.set(id, r.snoozed ? "snoozed" : "active");
    }
  }
  return status;
}

export function planFixedCostImport(
  fixedCosts: FixedCost[],
  existing: Pick<Row, "id" | "snoozed">[],
  month: string,
): FixedCostCandidate[] {
  const status = ledgerStatusByBaseId(existing);
  return fixedCosts
    .filter((cost) => Number(cost.amount) !== 0 && isDueInMonth(cost, month))
    .map((cost) => {
      const row = toLedgerRow(cost, month);
      return { row, inLedger: status.get(row.id) ?? "no" };
    })
    .sort((a, b) => a.row.dueDate.localeCompare(b.row.dueDate));
}

export function rowsToAdd(
  candidates: FixedCostCandidate[],
  selected: ReadonlySet<string>,
): Row[] {
  return candidates
    .filter((c) => selected.has(c.row.id))
    .map((c) => ({ ...c.row, id: `${c.row.id}.${newId()}` }));
}
